import React, { useContext, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  updateProfile,
} from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import {
  authErrorMessage,
  continueWithGoogle,
  ensureAccountProfile,
} from '../services/auth';
import AuthShell, { GoogleIcon } from './AuthShell';
import Header from './Header';
import Footer from './Footer';
import '../style/Auth.css';
import { useI18n } from '../i18n/I18nContext';

const ButtonSpinner = () => <span className="auth-button-spinner" aria-hidden="true" />;

const Register = () => {
  const { setIsLoggedIn, setUserType } = useContext(AuthContext);
  const { t, language } = useI18n();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const completeRegistration = (accountRole) => {
    setUserType(accountRole);
    setIsLoggedIn(true);
    navigate(accountRole === 'teller' ? '/onboarding/teller' : '/user-dashboard');
  };

  const validateConsent = () => {
    if (accepted) return true;
    setError(t('auth.consentError'));
    return false;
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    if (!validateConsent()) return;
    setLoading('email');
    setError('');
    let createdUser = null;
    try {
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      createdUser = credential.user;
      await updateProfile(createdUser, { displayName: name.trim() });
      const account = await ensureAccountProfile(createdUser, role, name);
      completeRegistration(account.role);
    } catch (registrationError) {
      if (createdUser) await deleteUser(createdUser).catch(() => null);
      setError(authErrorMessage(registrationError, language));
    } finally {
      setLoading('');
    }
  };

  const handleGoogleRegistration = async () => {
    if (!validateConsent()) return;
    setLoading('google');
    setError('');
    try {
      const account = await continueWithGoogle(role, name);
      completeRegistration(account.role);
    } catch (registrationError) {
      setError(authErrorMessage(registrationError, language));
    } finally {
      setLoading('');
    }
  };

  return (
    <div>
      <Header />
      <AuthShell
        eyebrow={t('auth.registerEyebrow')}
        title={t('auth.registerTitle')}
        description={t('auth.registerDescription')}
        storyTitle={t('auth.registerStory')}
        storyBody={t('auth.registerStoryBody')}
        alternateText={t('auth.existing')}
        alternateLabel={t('auth.login')}
        alternatePath="/login"
        raisedStory
      >
        <div className="account-role-picker" aria-label={t('auth.accountType')}>
          <button
            type="button"
            className={role === 'user' ? 'active' : ''}
            onClick={() => setRole('user')}
            disabled={Boolean(loading)}
            aria-pressed={role === 'user'}
          >
            <span>{t('auth.client')}</span>
            <small>{t('auth.clientHint')}</small>
          </button>
          <button
            type="button"
            className={role === 'teller' ? 'active' : ''}
            onClick={() => setRole('teller')}
            disabled={Boolean(loading)}
            aria-pressed={role === 'teller'}
          >
            <span>{t('auth.teller')}</span>
            <small>{t('auth.tellerHint')}</small>
          </button>
        </div>

        <button
          type="button"
          className="google-auth-button"
          onClick={handleGoogleRegistration}
          disabled={Boolean(loading)}
          aria-busy={loading === 'google'}
        >
          {loading === 'google' ? <ButtonSpinner /> : <GoogleIcon />}
          <span>{loading === 'google' ? t('auth.googleCreating') : t('auth.googleCreate')}</span>
        </button>

        <div className="auth-divider"><span>{t('auth.orCreate')}</span></div>

        {error && <div className="auth-feedback error" role="alert">{error}</div>}

        <form className="auth-form" onSubmit={handleRegister}>
          <label>
            {t('auth.fullName')}
            <input
              type="text"
              required
              maxLength="80"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('auth.namePlaceholder')}
              autoComplete="name"
            />
          </label>
          <label>
            {t('auth.email')}
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </label>
          <label>
            {t('auth.password')}
            <input
              type="password"
              required
              minLength="8"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t('auth.newPasswordPlaceholder')}
              autoComplete="new-password"
            />
          </label>
          <label className="auth-consent">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
            />
            <span>{t('auth.consent')}</span>
          </label>
          <button
            className="auth-submit"
            type="submit"
            disabled={Boolean(loading)}
            aria-busy={loading === 'email'}
          >
            {loading === 'email' && <ButtonSpinner />}
            <span>
              {loading === 'email' ? t('auth.creating') : role === 'teller' ? t('auth.createTeller') : t('auth.createClient')}
            </span>
          </button>
        </form>
      </AuthShell>
      <Footer />
    </div>
  );
};

export default Register;
